import {
  runInAction,
  makeAutoObservable,
  toJS,
  reaction,
  IReactionDisposer,
} from "mobx";
import { utils } from "@intrasync/utils";
import RootStore from "../stores/RootStore";
import { ServiceInterface } from "@feathersjs/feathers";
import ServiceStore from "./ServiceStore";

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  adminUser: boolean;
  department: string;
  team: string;
  languagePreference: string;
}

interface AuthStoreOptions {
  idField?: string;
  userService?: string;
  entityField?: string;
  debugMode?: boolean;
  authStorageKey?: string;
  refreshStorageKey?: string;
}

interface Notification {
  message: string;
  options: {
    variant: string;
  };
}

interface HandleNotAuthenticatedParams {
  name: string;
  message: string;
}

interface UpdateUser {
  id?: string;
  [key: string]: unknown;
}

interface AuthenticateHandlerData {
  accessToken: string;
  refreshToken?: string;
  [key: string]: unknown;
}

interface AuthValue {
  strategy?: string;
  apiKey?: string;
  email?: string;
  password?: string;
}

export type AuthResponse = {
  refreshToken?: string;
  accessToken?: string;
  user?: User;
};

interface AuthProps {
  refreshToken?: string;
  jwt?: string;
  userId?: number;
  deviceId?: string;
}

interface TokenResponse {
  accessToken: string;
}

interface UserPermission {
  userId: number;
  appName: string;
  permission: string;
  location: string;
}

interface UserSubscription {
  appName: string;
  userId: number;
  entities?: UserSubscription[];
  find?: (query: object) => Promise<UserSubscription[]>;
}

// export type AuthStoreOptions = {
//   idField: string;
//   userService: string;
//   entityField: string;
//   debugMode: boolean;
//   authStorageKey: string;
//   refreshStorageKey: string;
// };

declare const window: Window;

class AuthStore {
  storeLoaded = false;
  loadingAuthStorage = false;

  idField: string = "id";
  userService = "user";
  entityField = "user";
  options: AuthStoreOptions = {};

  // Track if trying to login and errors
  isAuthenticatePending: boolean = false;
  errorOnAuthenticate: Error | null = null;

  // Track if trying to logout and errors
  isLogoutPending: boolean = false;
  errorOnLogout: Error | null = null;

  token = "";
  refreshToken = "";
  deviceId = "";
  isSignedIn = false;
  user: User | undefined;

  sessionId = utils.uuid();

  rootStore: RootStore;

  debugMode: boolean = false;
  storage: Storage | null = null;
  refreshTokenService: ServiceInterface;
  authStorageKey: string;
  refreshStorageKey: string;
  duoRedirectLink: string;
  socketAuthTimeout: NodeJS.Timeout | null = null;
  getUserPermissionsDispose: IReactionDisposer | null = null;
  errorOnAuthenticateDispose: IReactionDisposer | null = null;
  userPermissionsDispose: IReactionDisposer | null = null;
  currentUserDispose: IReactionDisposer | null = null;
  tokenRefreshTimer: NodeJS.Timeout | null = null;
  tokenExpiryTime: number = 0;
  authStateCache: {
    lastAuthCheck: number;
    isValid: boolean;
    cacheTimeout: number;
  } = {
    lastAuthCheck: 0,
    isValid: false,
    cacheTimeout: 30000, // 30 seconds cache
  };

  authTimingMetrics: {
    initStartTime: number;
    initEndTime: number;
    loadStorageTime: number;
    authenticateStartTime: number;
    authenticateEndTime: number;
  } = {
    initStartTime: 0,
    initEndTime: 0,
    loadStorageTime: 0,
    authenticateStartTime: 0,
    authenticateEndTime: 0,
  };

  /**
   * @constructor constructor for authStore class, initializes stores
   * and feather services
   */
  constructor(rootStore: RootStore, options: AuthStoreOptions) {
    this.authTimingMetrics.initStartTime = performance.now();
    this.rootStore = rootStore || null;
    this.idField = options.idField || this.idField;
    this.userService = options.userService || this.userService;
    this.entityField = options.entityField || this.entityField;
    this.storage = (rootStore.storage as Storage) || null;
    this.debugMode = options.debugMode || false;
    this.refreshTokenService = rootStore.app.service("refresh-token");
    this.authStorageKey = options.authStorageKey || "feathers-jwt-dev";
    this.refreshStorageKey = options.refreshStorageKey || "refresh-token-dev";

    this.duoRedirectLink = "";

    makeAutoObservable(this, {
      rootStore: false,
    });

    // Start async initialization - don't block constructor
    this.initializeAsync();

    if (rootStore.app?.io) {
      this.debugMode && console.log("rootStore.app.io setting up listeners");
      rootStore.app.io.on("connect", () => {
        this.debugMode && console.log("app.io connect event");
        return this.handleFistLoad();
      });

      rootStore.app.io.on("reconnect", () => {
        this.debugMode && console.log("app.io reconnect event");
        return this.handleFistLoad();
      });

      rootStore.app.io.on("disconnect", async () => {
        this.debugMode && console.log("app.io disconnect event");
        if (this.isSignedIn) this.setIsSignedIn(false);
      });
      if (rootStore.app.service("email-login")) {
        this.setupEmailLogin();
      }
    }

    rootStore.app.on("login", (auth: AuthenticateHandlerData) => {
      // console.log('app.io login event', auth);
      this.debugMode && console.log("app.io login event", auth);
      if (!this.isSignedIn) this.setIsSignedIn(true);
      this.authenticateHandler(auth);
      if (this.rootStore.jobStore) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-expect-error
        this.rootStore.jobStore.find();
      }
    });

    rootStore.app.on("logout", () => {
      this.debugMode && console.log("app.io logout event");
      if (this.isSignedIn) this.setIsSignedIn(false);
      this.logout();
    });

    if (rootStore.userPermissionsStore) {
      this.getUserPermissionsDispose = reaction(
        () => this.userId,
        (newUserId: number) => {
          if (newUserId) {
            this.getUserPermissions(newUserId);
          }
        }
      );
    }
    if (rootStore.mode !== "test") {
      this.debugMode && console.log("Debug enabling channels");
      // TODO - Enable this when we have a channel service fixed
      // rootStore.app.service('user').on('patched', (patchedUser: any) => {
      //   console.log('Debug user patched', patchedUser);
      //   if (
      //     patchedUser[this.idField as keyof User] ===
      //     this.currentUser[this.idField as keyof User]
      //   ) {
      //     this.updateUser(patchedUser);
      //     this.getUserPermissions(patchedUser[this.idField]);
      //   }
      // });
      // rootStore.app
      //   .service(this.userService)
      //   .on('removed', (removedUser: any) => {
      //     this.debugMode && console.log('Debug user removed', removedUser);
      //     if (
      //       removedUser[this.idField as keyof User] ===
      //       this.currentUser[this.idField as keyof User]
      //     )
      //       this.logout();
      //   });
      // console.log('setting up refresh-token listener for socketAuth');
      if (rootStore.app.service("refresh-token")) {
        rootStore.app
          .service("refresh-token")
          .on(
            "socketAuth",
            (socketAuth: {
              removeAuthTokens?: boolean;
              accessToken?: string;
            }) => {
              this.debugMode && console.log("Debug socketAuth", socketAuth);
              // this.setIsSignedIn(true);
              if (socketAuth.removeAuthTokens) {
                return this.logout();
              }
              if (socketAuth.accessToken) {
                return this.handleRefreshTokenAuth(socketAuth.accessToken);
              }
            }
          );
      }
    }
    if (
      rootStore.app.authentication &&
      rootStore.app.authentication.authenticated
      // ||      rootStore.mode === 'test'
    ) {
      this.debugMode &&
        console.log("Debug: App Authenticated, Setting isSignedIn");
      this.setIsSignedIn(true);
      if (!this.user) {
        console.warn(
          "should probably set user from app.authentication as well"
        );
      }
    }

    this.errorOnAuthenticateDispose = reaction(
      () => this.errorOnAuthenticate,
      (authenticationError: Error) => {
        if (authenticationError) {
          const { name, message } = authenticationError;
          if (name === "NotAuthenticated") {
            this.handleNotAuthenticated({ name, message });
          } else {
            this.handleErrorNotification(name, message);
          }
          console.error("authenticationError", authenticationError);
        }
      },
      { fireImmediately: true }
    );
  }

  /**
   * Async initialization method to prevent blocking constructor
   */
  private initializeAsync = async () => {
    try {
      await this.loadAuthStorage();

      // Setup socket authentication timeout after storage is loaded
      this.setupSocketAuthTimeout();

      this.authTimingMetrics.initEndTime = performance.now();
      const initTime =
        this.authTimingMetrics.initEndTime -
        this.authTimingMetrics.initStartTime;

      this.debugMode &&
        console.log(
          `AuthStore async initialization completed in ${Math.round(
            initTime
          )}ms`
        );

      // Log performance metrics in development
      if (this.rootStore.mode === "development" && this.debugMode) {
        console.log("AuthStore Performance Metrics:", {
          totalInitTime: Math.round(initTime),
          storageLoadTime: Math.round(this.authTimingMetrics.loadStorageTime),
          authInitTime: Math.round(
            initTime - this.authTimingMetrics.loadStorageTime
          ),
        });
      }
    } catch (error) {
      console.error("AuthStore initialization failed:", error);
      this.setErrorOnAuth(error as Error);
    }
  };

  /**
   * Setup socket authentication with timeout
   */
  private setupSocketAuthTimeout = () => {
    if (this.rootStore.app && this.rootStore.app.get) {
      const socketAuthPending = this.rootStore.app.get("socketAuthPending");
      if (socketAuthPending) {
        runInAction(() => {
          this.isAuthenticatePending = true;
        });

        // Reduced timeout from 8 seconds to 3 seconds for faster feedback
        this.socketAuthTimeout = setTimeout(() => {
          if (this.isAuthenticatePending || !this.isSignedIn) {
            // Reset isAuthenticatePending after 3 seconds
            runInAction(() => {
              this.isAuthenticatePending = false;
              this.handleConnect().catch((err: Error) => {
                console.warn("Error: handleConnect socketAuthPending", err);
                if (err.message === "Redirect to login") {
                  if (this.rootStore.routerStore) {
                    this.rootStore.routerStore?.goTo("LoginPage");
                  }
                }
              });
            });
          }
        }, 3000); // Reduced from 8000ms to 3000ms
      }
    }
  };

  handleFistLoad = async () => {
    // console.log('handleFistLoad');
    return await this.handleConnect().catch((err: Error) => {
      console.warn("Error: handleConnect app.onConnect", err);
      if (err.message === "Redirect to login") {
        if (this.rootStore.routerStore) {
          this.rootStore.routerStore?.goTo("LoginPage");
        }
      }
    });
  };

  handleErrorNotification = (name: string, message: string): string => {
    console.warn("handleErrorNotification", name, message);
    if (this.rootStore.notificationStore) {
      const notification: Notification = {
        message: message,
        options: { variant: "warning" },
      };
      this.rootStore.notificationStore.addNotification(notification);
    }
    return message;
  };

  handleNotAuthenticated = async ({
    name,
    message,
  }: HandleNotAuthenticatedParams): Promise<void> => {
    this.handleErrorNotification(name, message);
    if (this.rootStore.routerStore) {
      const { handleAuthReroute, currentRoute, params, queryParams } =
        this.rootStore.routerStore;
      const checkRefresh = await this.routerStoreAuthCheck();
      if (!checkRefresh) {
        if (currentRoute?.key) {
          handleAuthReroute(currentRoute, params, queryParams);
        }
        return;
      }
    }
  };

  handleConnect = async () => {
    const { app } = this.rootStore;
    console.log("handleConnect");
    console.log("app.authentication", toJS(app.authentication));
    console.log("this.isSignedIn", this.isSignedIn);

    const isAuthenticated = app.authentication?.authenticated;
    console.log("isAuthenticated", isAuthenticated);

    if (isAuthenticated && !this.isSignedIn) {
      console.log(
        "handleConnect was called and the authStore is out of sync with the app authentication"
      );
      this.setIsSignedIn(true);
      if (
        (!this.user || (this.user as User).id === undefined) &&
        (app.authentication?.options?.storage as unknown as { userId: number })
          ?.userId
      ) {
        this.debugMode &&
          console.log(
            "handleConnect was called and the authStore user had not been set"
          );
        const foundUser = await app
          .service(this.userService)
          .get(
            (
              app.authentication.options.storage as unknown as {
                userId: number;
              }
            ).userId
          )
          .catch(() => {
            return Promise.reject("Failed to get user");
          });
        if (foundUser) {
          this.updateUser(foundUser);
        }
      }
      return Promise.resolve("221");
    }
    const currentRoute = this.rootStore.routerStore?.currentRoute;
    const isOnLoginPage =
      currentRoute?.key === "LoginPage" || currentRoute?.name === "LoginPage";
    if (app.authentication.authenticated && this.isSignedIn) {
      // Check if user is on LoginPage

      if (!isOnLoginPage) {
        console.log(
          "handleConnect cant do anything yet because user is already signed in and not on LoginPage"
        );
        return Promise.resolve("228");
      }

      // If user is signed in but on LoginPage, continue to potentially redirect them
      console.log("User is signed in but on LoginPage, continuing...");
    }
    if (this.isAuthenticatePending) {
      console.log("handleConnect cant do anything yet because auth is pending");

      // Wait for authentication to complete (with timeout)
      const maxWaitTime = 10000; // 10 seconds timeout
      const checkInterval = 100; // Check every 100ms
      let waitedTime = 0;

      return new Promise((resolve, reject) => {
        const checkAuthStatus = setInterval(() => {
          waitedTime += checkInterval;

          // Check if auth is no longer pending
          if (!this.isAuthenticatePending) {
            clearInterval(checkAuthStatus);
            console.log("Authentication completed, retrying handleConnect");

            // Recursively call handleConnect now that auth is done
            this.handleConnect().then(resolve).catch(reject);
          }

          // Timeout after maxWaitTime
          if (waitedTime >= maxWaitTime) {
            clearInterval(checkAuthStatus);
            console.warn("Authentication timeout - took longer than expected");
            reject("Authentication timeout");
          }
        }, checkInterval);
      });
    }
    if (!this.storeLoaded) {
      console.log(
        "handleConnect cant do anything yet because store isnt loaded"
      );
      return Promise.reject("Store has not finished loading");
    }
    try {
      // Before we do anything let's make sure we arent trying to load from ExternalAuth
      if (
        this.rootStore &&
        this.rootStore.routerStore &&
        this.rootStore.routerStore.firstLoadRoute &&
        this.rootStore.routerStore.firstLoadRoute
      ) {
        console.log(
          "firstLoadRoute",
          this.rootStore.routerStore.firstLoadRoute
        );
        console.log(
          "firstLoadQueryParams",
          this.rootStore.routerStore.firstLoadQueryParams
        );

        const {
          routerStore: { goTo, firstLoadRoute, firstLoadQueryParams },
        } = this.rootStore;
        if (
          firstLoadRoute === "ExternalAuth" ||
          (typeof firstLoadRoute === "object" &&
            (firstLoadRoute as { key: string }).key === "ExternalAuth")
        ) {
          if (
            firstLoadQueryParams &&
            (firstLoadQueryParams as { accessToken?: string }).accessToken
          ) {
            return goTo("ExternalAuth", {}, { ...firstLoadQueryParams });
          }
        }
        if (
          firstLoadRoute === "LoginPage" ||
          (typeof firstLoadRoute === "object" &&
            firstLoadRoute !== null &&
            "key" in firstLoadRoute &&
            (firstLoadRoute as { key: string }).key === "LoginPage")
        ) {
          console.log("User is on LoginPage with firstLoadRoute");
          if (
            firstLoadQueryParams &&
            (firstLoadQueryParams as { completePasswordReset?: boolean })
              .completePasswordReset
          ) {
            console.log("User needs to complete password reset");
            return goTo("LoginPage", {}, { ...firstLoadQueryParams });
          }
          if (
            firstLoadQueryParams &&
            (firstLoadQueryParams as { activation?: boolean }).activation
          ) {
            return goTo("LoginPage", {}, { ...firstLoadQueryParams });
          }
          if (
            isAuthenticated &&
            this.isSignedIn &&
            (firstLoadQueryParams as { returnUrl?: string })?.returnUrl
          ) {
            console.log("User has returnUrl, redirecting...");
            const returnUrl = (
              firstLoadQueryParams as { returnUrl?: string }
            ).returnUrl.replace("/app/", "");
            console.log("returnUrl", returnUrl);
            return this.rootStore.routerStore?.goToUrl(returnUrl as string);
          }
        }
      } else if (isOnLoginPage) {
        console.log("User is on LoginPage");
        this.rootStore.routerStore.goTo("Home");
      }
      this.debugMode && console.log("handleConnect reAuthenticating");
      if (!this.refreshToken || !this.userId || !this.deviceId || !this.token) {
        this.debugMode &&
          console.log(
            "handleConnect is missing one of the needed reAuth items"
          );
        const loaded = await this.loadAuthStorage();
        this.debugMode && console.log("handleConnect loaded", loaded);
        if (loaded && !loaded.refreshToken && !loaded.token) {
          this.debugMode &&
            console.log(
              "handleConnect loaded but had no token or refreshToken"
            );

          return Promise.reject("Redirect to login");
        }

        if (
          loaded &&
          loaded.token &&
          loaded.userId &&
          loaded.deviceId &&
          !loaded.refreshToken
        ) {
          this.debugMode &&
            console.log("DebugMode handleConnect loaded with values");
          this.debugMode &&
            console.log(
              "loaded.token, loaded.userId, loaded.deviceId",
              loaded.token,
              loaded.userId,
              loaded.deviceId
            );
          await this.reAuthenticate({
            jwt: loaded.token,
            userId: loaded.userId,
            deviceId: loaded.deviceId,
          });
        }
        if (
          loaded &&
          loaded.refreshToken &&
          !loaded.token &&
          loaded.userId &&
          loaded.deviceId
        ) {
          this.debugMode &&
            console.log("DebugMode handleConnect loaded with values");
          this.debugMode &&
            console.log(
              "loaded.refreshToken, loaded.userId, loaded.deviceId",
              loaded.refreshToken,
              loaded.userId,
              loaded.deviceId
            );
          await this.reAuthenticate({
            refreshToken: loaded.refreshToken,
            userId: loaded.userId,
            deviceId: loaded.deviceId,
          });
        }
      } else {
        this.debugMode &&
          console.log("DebugMode handleConnect loaded with values");
        this.debugMode &&
          console.log(
            "this.refreshToken, this.userId, this.deviceId",
            this.refreshToken,
            this.userId,
            this.deviceId
          );
        await this.reAuthenticate({
          refreshToken: this.refreshToken,
          userId: this.userId,
          deviceId: this.deviceId,
        });
      }
      return Promise.resolve("316");
    } catch (err) {
      console.error("handleConnect Error", err);
      return Promise.reject("Handle Connect Could Not Process Request");
    }
  };

  loadAuthStorage = async () => {
    const loadStartTime = performance.now();
    this.loadingAuthStorage = true;
    this.debugMode &&
      console.log("Debug loadAuthStorage - starting batched load");

    if (!this.storage) {
      console.log("Debug loadAuthStorage - No Store to load from");
      return;
    }

    try {
      // Batch localStorage reads to minimize blocking time
      const userIdKey = this.rootStore.mode.includes("prod")
        ? "userId"
        : `userId-${this.rootStore.mode}`;

      // Single batched read of all auth data
      const [loadToken, loadRefreshToken, loadUserId, deviceId] =
        await Promise.all([
          Promise.resolve(window.localStorage.getItem(this.authStorageKey)),
          Promise.resolve(window.localStorage.getItem(this.refreshStorageKey)),
          Promise.resolve(window.localStorage.getItem(userIdKey)),
          Promise.resolve(window.localStorage.getItem("deviceId")),
        ]);

      // Single runInAction for all state updates
      runInAction(() => {
        // Set token if available and setup refresh timer
        if (loadToken) {
          this.debugMode && console.log("Debug async loadToken", loadToken);
          this.token = loadToken;
          this.setupTokenRefreshTimer(loadToken);
        }

        // Set refresh token if available (without triggering storage write)
        if (loadRefreshToken) {
          this.debugMode &&
            console.log("Debug async loadRefreshToken", loadRefreshToken);
          this.refreshToken = loadRefreshToken;
        }

        // Set user ID if available
        if (loadUserId) {
          this.debugMode && console.log("Debug async loadUserId", loadUserId);
          if (!this.userId || this.userId !== Number(loadUserId)) {
            this.updateUser({ id: loadUserId });
          }
        }

        // Set or generate device ID
        if (deviceId) {
          this.debugMode && console.log("Debug async deviceId", deviceId);
          this.deviceId = deviceId;
        } else {
          const newDeviceId = utils.uuid();
          this.deviceId = newDeviceId;
          // Defer storage write to not block loading
          Promise.resolve().then(() => {
            window.localStorage.setItem("deviceId", newDeviceId);
          });
          this.debugMode &&
            console.log("Debug async made new deviceId", newDeviceId);
        }

        // Mark store as loaded
        this.storeLoaded = true;
        this.loadingAuthStorage = false;
      });

      const loadEndTime = performance.now();
      this.authTimingMetrics.loadStorageTime = loadEndTime - loadStartTime;

      this.debugMode &&
        console.log(
          `Debug loadAuthStorage - completed batched load in ${Math.round(
            this.authTimingMetrics.loadStorageTime
          )}ms`
        );

      return {
        deviceId: this.deviceId,
        userId: this.userId,
        refreshToken: this.refreshToken,
        token: this.token,
      };
    } catch (err) {
      console.error("loadAuthStorage Error", err);
      runInAction(() => {
        this.loadingAuthStorage = false;
        this.storeLoaded = true; // Still mark as loaded to prevent blocking
      });
      throw err;
    }
  };

  setupEmailLogin = () => {
    this.debugMode && console.log("Setting up email login listener");
    const emailLoginService = this.rootStore.app.service("email-login");
    emailLoginService.on(
      "loginComplete",
      async (auth: AuthenticateHandlerData) => {
        this.debugMode && console.log("LIVE DEBUG loginComplete!");
        this.debugMode && console.log("LIVE DEBUG auth", auth);
        const connections = [this.sessionId];
        if (this.rootStore.app.io.id) {
          connections.push(this.rootStore.app.io.id);
        }
        if (this.rootStore?.channelStore?.websocketConnection) {
          connections.push(this.rootStore.channelStore.websocketConnection);
        }
        if (
          auth &&
          auth.connectionId &&
          auth.accessToken &&
          connections.includes(auth.connectionId as string)
        ) {
          this.debugMode && console.log("LIVE DEBUG IS CURRENT!!!");
          const { accessToken } = auth;
          this.debugMode && console.log("LIVE DEBUG accessToken", accessToken);
          await this.rootStore.app
            .authenticate({
              strategy: "jwt",
              deviceId: this.deviceId,
              accessToken: accessToken,
            })
            .then(async () => {
              if (this.rootStore.routerStore) {
                this.debugMode &&
                  console.log("LIVE DEBUG routing user to home");
                console.log("goTo in authStore");
                return this.rootStore.routerStore?.goTo(
                  this.rootStore.routerStore.defaultRoute || "Home"
                );
              }
            });
        }
      }
    );
  };

  createDuoAuthRedirect = async ({ email }: { email: string }) => {
    try {
      const { authRedirectStore, appName } = this.rootStore;
      if (!email) return new Error("Requires authRedirectStore");
      if (!authRedirectStore) return new Error("Requires authRedirectStore");
      return await authRedirectStore.create({
        email: email,
        uuid: utils.uuid(),
        appName: appName || "unknown",
        connection:
          this.rootStore.app?.io?.id ||
          this.rootStore.channelStore?.websocketConnection ||
          this.sessionId,
      });
    } catch (err) {
      console.error("Something went wrong with createDuoAuthRedirect", err);
    }
  };

  setIsSignedIn = (value: boolean) => {
    this.isSignedIn = value;
    // Invalidate cache when sign-in state changes
    this.invalidateAuthCache();
  };

  handleDebugMode = () => {
    this.debugMode = !this.debugMode;
  };

  dispose = () => {
    console.log("DISPOSING AUTH STORE");
    this.userPermissionsDispose && this.userPermissionsDispose();
    this.currentUserDispose && this.currentUserDispose();
    this.getUserPermissionsDispose && this.getUserPermissionsDispose();
    this.errorOnAuthenticateDispose && this.errorOnAuthenticateDispose();

    this.socketAuthTimeout && clearTimeout(this.socketAuthTimeout);
    this.tokenRefreshTimer && clearTimeout(this.tokenRefreshTimer);

    this.rootStore.app.removeListener("login", () =>
      console.log("removed listener for login")
    );
    this.rootStore.app.removeListener("logout", () =>
      console.log("removed listener for logout")
    );
    if (this.rootStore.mode !== "test") {
      const userServiceDispose = this.rootStore.app.service(this.userService);
      userServiceDispose.removeListener("patched", () =>
        console.log("removed listener for patched")
      );
      userServiceDispose.removeListener("removed", () =>
        console.log("removed listener for removed")
      );
    }
  };

  /**
   * Parse JWT token to extract expiry time
   */
  private parseTokenExpiry = (token: string): number => {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) return 0;

      const payload = JSON.parse(atob(parts[1]));
      return payload.exp ? payload.exp * 1000 : 0; // Convert to milliseconds
    } catch (error) {
      this.debugMode && console.warn("Failed to parse token expiry:", error);
      return 0;
    }
  };

  /**
   * Setup proactive token refresh timer
   */
  private setupTokenRefreshTimer = (token: string) => {
    // Clear existing timer
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }

    const expiryTime = this.parseTokenExpiry(token);
    if (!expiryTime) return;

    this.tokenExpiryTime = expiryTime;
    const currentTime = Date.now();
    const timeUntilExpiry = expiryTime - currentTime;

    // Refresh 5 minutes before expiry (300000ms)
    const refreshTime = Math.max(timeUntilExpiry - 300000, 60000); // Minimum 1 minute

    if (refreshTime > 0 && this.refreshToken) {
      this.tokenRefreshTimer = setTimeout(() => {
        this.debugMode && console.log("Proactive token refresh triggered");
        this.proactiveTokenRefresh().catch((error) => {
          console.warn("Proactive token refresh failed:", error);
        });
      }, refreshTime);

      this.debugMode &&
        console.log(
          `Token refresh scheduled in ${Math.round(
            refreshTime / 1000 / 60
          )} minutes`
        );
    }
  };

  /**
   * Perform proactive token refresh
   */
  private proactiveTokenRefresh = async () => {
    if (!this.refreshToken || !this.userId || !this.deviceId) {
      this.debugMode && console.warn("Insufficient data for proactive refresh");
      return;
    }

    try {
      this.debugMode && console.log("Performing proactive token refresh");

      if (!this.refreshTokenService) {
        throw new Error("Refresh token service not available");
      }

      const tokenResponse = await this.refreshTokenService!.create({
        userId: Number(this.userId),
        refreshToken: this.refreshToken,
        deviceId: this.deviceId,
      });

      if (tokenResponse.accessToken) {
        runInAction(() => {
          this.token = tokenResponse.accessToken;
          // Setup next refresh cycle
          this.setupTokenRefreshTimer(tokenResponse.accessToken);
        });

        this.debugMode && console.log("Proactive token refresh successful");
      }
    } catch (error) {
      console.warn("Proactive token refresh failed:", error);
      // Don't set error state for proactive refresh failures
      // Let normal auth flow handle it
    }
  };

  /**
   * Check if token is close to expiry (within 10 minutes)
   */
  get isTokenNearExpiry(): boolean {
    if (!this.tokenExpiryTime) return false;
    const currentTime = Date.now();
    const timeUntilExpiry = this.tokenExpiryTime - currentTime;
    return timeUntilExpiry < 600000; // 10 minutes
  }

  /**
   * Check if authentication state cache is valid
   */
  private isAuthCacheValid(): boolean {
    const currentTime = Date.now();
    return (
      currentTime - this.authStateCache.lastAuthCheck <
      this.authStateCache.cacheTimeout
    );
  }

  /**
   * Update authentication state cache
   */
  private updateAuthCache(isValid: boolean): void {
    this.authStateCache.lastAuthCheck = Date.now();
    this.authStateCache.isValid = isValid;
  }

  /**
   * Fast cached authentication check
   */
  get isCachedAuthValid(): boolean {
    if (this.isAuthCacheValid()) {
      return this.authStateCache.isValid;
    }

    // Cache is expired, perform new check
    const isValid = !!(
      this.token &&
      this.userId &&
      this.deviceId &&
      !this.isTokenNearExpiry
    );
    this.updateAuthCache(isValid);
    return isValid;
  }

  /**
   * Invalidate authentication cache (call when auth state changes)
   */
  private invalidateAuthCache(): void {
    this.authStateCache.lastAuthCheck = 0;
    this.authStateCache.isValid = false;
  }

  setErrorOnAuth = (error: Error) => {
    this.debugMode && console.log("Debug setErrorOnAuth", error);
    this.errorOnAuthenticate = error;
  };

  setErrorOnLogout = (error: Error) => {
    this.debugMode && console.log("Debug setErrorOnLogout", error);
    this.errorOnLogout = error;
  };

  clearPending = () => {
    this.debugMode && console.log("Debug clearPending");
    this.isAuthenticatePending = false;
    this.isLogoutPending = false;
  };

  clearErrors = () => {
    this.debugMode && console.log("Debug clearErrors");
    this.errorOnAuthenticate = null;
    this.errorOnLogout = null;
  };

  clearStore = () => {
    this.debugMode && console.log("Debug clearStore");
    this.token = "";
    this.refreshToken = "";
    this.user = undefined;
    // Invalidate cache when clearing store
    this.invalidateAuthCache();
  };

  clearStorage = async () => {
    this.debugMode && console.log("Debug clearStorage authStore");

    if (window.localStorage) {
      try {
        this.debugMode && console.log("Debug clearStorage async");
        window.localStorage.removeItem(this.refreshStorageKey);
        const userIdKey = this.rootStore.mode.includes("prod")
          ? "userId"
          : `userId-${this.rootStore.mode}`;
        window.localStorage.removeItem(userIdKey);
        window.localStorage.removeItem(this.authStorageKey);
        return;
      } catch (err) {
        this.setErrorOnAuth(err as Error);
      }
    }
  };

  clearAll = async () => {
    this.debugMode && console.log("Debug clearAll");

    // Clear token refresh timer
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }

    this.clearStore();
    this.clearErrors();
    this.clearPending();
    await this.clearStorage();
  };

  updateUser = async (updateUser: UpdateUser): Promise<void> => {
    this.debugMode && console.log("Debug updateUser", updateUser);
    const currentAuthUser: User = toJS(this.user) || ({} as User);
    if (updateUser.id && Number(updateUser.id) !== this.userId) {
      const userIdKey = this.rootStore.mode.includes("prod")
        ? "userId"
        : `userId-${this.rootStore.mode}`;
      window.localStorage.setItem(userIdKey, updateUser.id);
    }
    this.user = Object.assign(currentAuthUser, {
      ...updateUser,
      id: updateUser.id || currentAuthUser.id,
      email: (updateUser.email as string) || currentAuthUser.email,
      firstName: (updateUser.firstName as string) || currentAuthUser.firstName,
      lastName: (updateUser.lastName as string) || currentAuthUser.lastName,
      adminUser: (updateUser.adminUser as boolean) ?? currentAuthUser.adminUser,
    });
  };

  authenticateHandler = async (data: AuthenticateHandlerData) => {
    this.debugMode && console.log("Debug authenticateHandler", data);
    this.token = data.accessToken;

    // Setup proactive token refresh for new access token
    if (data.accessToken) {
      this.setupTokenRefreshTimer(data.accessToken);
    }

    // Invalidate cache when new authentication occurs
    this.invalidateAuthCache();

    if (data.refreshToken) {
      if (data.refreshToken !== this.refreshToken) {
        this.debugMode &&
          console.log(
            "Debug authenticateHandler has a new refreshToken",
            data.refreshToken
          );
        this.setRefreshToken(data.refreshToken);
      }
    }

    const currentUserData = data[this.entityField] as unknown as User;

    if (currentUserData) {
      this.debugMode &&
        console.log("Debug authenticateHandler setting user", currentUserData);
      this.updateUser(currentUserData as unknown as UpdateUser);
    } else if (
      this.userService &&
      Object.prototype.hasOwnProperty.call(data, this.idField) &&
      (currentUserData as User).id
    ) {
      try {
        const foundUser = await this.rootStore.app
          .service(this.userService)
          .get((currentUserData as User).id);
        runInAction(() => {
          this.updateUser(foundUser);
          this.debugMode &&
            console.log("Debug authenticateHandler setting user", foundUser);
        });
      } catch (err) {
        this.setErrorOnAuth(err as Error);
        this.isAuthenticatePending = false;
      }
    }
    if (!this.isSignedIn) {
      this.setIsSignedIn(true);
    }
    this.getUserPermissions(currentUserData.id);
    this.getUserSubscriptions(currentUserData.id);

    if (this.isAuthenticatePending) {
      this.isAuthenticatePending = false;
    }
  };

  getUserPermissions = async (userId: number) => {
    let permissionUserId = userId;
    if (!permissionUserId && this.userId) {
      permissionUserId = toJS(this.userId);
    }
    if (
      !permissionUserId ||
      !this.rootStore ||
      !this.rootStore.userPermissionsStore ||
      !this.rootStore.userPermissionsStore.find
    ) {
      if (!permissionUserId)
        console.warn("getUserSubscriptions missing permissionUserId");
      if (!this.rootStore)
        console.warn("getUserSubscriptions missing this.rootStore");
      if (
        !this.rootStore.userPermissionsStore ||
        !this.rootStore.userPermissionsStore.find
      )
        console.warn(
          "getUserSubscriptions missing this.rootStore.userPermissionsStore"
        );

      return;
    }
    this.debugMode &&
      console.log(
        "Debug getUserPermissions permissionUserId",
        permissionUserId
      );
    try {
      await this.rootStore.userPermissionsStore.find({
        query: { userId: permissionUserId, appName: this.rootStore.appName },
      });
    } catch (err) {
      console.error("Error getUserPermissions", err);
    }
  };

  getUserSubscriptions = async (userId: number | string): Promise<void> => {
    let subscriptionUserId = userId;
    if (!subscriptionUserId && this.userId) {
      subscriptionUserId = this.userId;
    }
    if (
      !subscriptionUserId ||
      !this.rootStore ||
      !this.rootStore.userSubscriptionStore ||
      !(
        this.rootStore.userSubscriptionStore &&
        typeof this.rootStore.userSubscriptionStore === "object" &&
        this.rootStore.userSubscriptionStore !== null &&
        "find" in (this.rootStore.userSubscriptionStore as object)
      )
    ) {
      if (!subscriptionUserId)
        console.warn(
          "getUserSubscriptions missing subscriptionUserId",
          !subscriptionUserId
        );
      if (!this.rootStore)
        console.warn("getUserSubscriptions missing this.rootStore");
      if (
        !this.rootStore.userSubscriptionStore ||
        !(
          this.rootStore.userSubscriptionStore &&
          typeof this.rootStore.userSubscriptionStore === "object" &&
          "find" in this.rootStore.userSubscriptionStore
        )
      )
        console.warn(
          "getUserSubscriptions missing this.rootStore.userSubscriptionStore"
        );

      return;
    }
    this.debugMode &&
      console.log(
        "Debug getUserSubscriptions subscriptionUserId",
        subscriptionUserId
      );
    try {
      if (!this.rootStore.userSubscriptionStore) {
        console.warn("getUserSubscriptions missing userSubscriptionStore");
        return;
      }
      await (this.rootStore?.userSubscriptionStore as UserSubscription)?.find?.(
        {
          query: {
            userId: subscriptionUserId,
            appName: this.rootStore.appName,
          },
        }
      );
    } catch (err) {
      console.error("Error getUserSubscriptions", err);
    }
  };

  setRefreshToken = async (refreshToken: string) => {
    this.debugMode && console.log("Debug setRefreshToken", refreshToken);
    try {
      window.localStorage.setItem(this.refreshStorageKey, refreshToken);
    } catch (err) {
      this.setErrorOnAuth(err as Error);
    }
    this.refreshToken = refreshToken;
  };

  setUserId = async () => {
    throw new Error("Deprecated");
  };

  /**
   * @function authenticate
   * @param email
   * @param password
   * @description authenticates user with password
   */
  authenticate = async (
    authValue: AuthValue
  ): Promise<AuthResponse | undefined> => {
    this.authTimingMetrics.authenticateStartTime = performance.now();
    this.debugMode && console.log("Debug authenticate", authValue);
    this.isAuthenticatePending = true;
    let response: AuthResponse | undefined;
    try {
      if (authValue.strategy) {
        if (authValue.strategy === "apiKey") {
          this.debugMode && console.log("Debug authenticating with apiKey");
          response = await this.rootStore.app.authenticate({
            strategy: authValue.strategy,
            apiKey: authValue.apiKey,
            deviceId: this.deviceId || "",
          });
        } else {
          this.debugMode &&
            console.log(
              "Debug authenticating with strategy",
              authValue.strategy
            );
          response = await this.rootStore.app.authenticate({
            ...authValue,
            deviceId: this.deviceId || "",
          });
        }
      } else {
        this.debugMode && console.log("Debug authenticating with credentials");
        this.debugMode && console.log("Debug sending deviceId", this.deviceId);

        console.log("authValue", authValue);

        response = await this.rootStore.app.authenticate({
          strategy: "local",
          email: authValue.email,
          password: authValue.password,
          deviceId: this.deviceId || "",
        });
      }
      runInAction(() => {
        this.authTimingMetrics.authenticateEndTime = performance.now();
        const authTime =
          this.authTimingMetrics.authenticateEndTime -
          this.authTimingMetrics.authenticateStartTime;

        this.debugMode &&
          console.log(
            `Debug authentication response in ${Math.round(authTime)}ms`,
            response
          );
        this.isAuthenticatePending = false;

        if (
          response?.refreshToken &&
          response.refreshToken !== this.refreshToken
        )
          this.setRefreshToken(response.refreshToken);
        if (response?.user && response.user.id !== this.userId) {
          this.updateUser(response.user as unknown as UpdateUser);
        }
      });
    } catch (err) {
      runInAction(() => {
        this.isAuthenticatePending = false;
        this.setErrorOnAuth(err as Error);
      });
    }
    return response;
  };

  routerStoreAuthCheck = async () => {
    this.debugMode && console.log("Debug routerStoreAuthCheck");

    // Fast path: check cached auth state first
    if (this.isCachedAuthValid && this.isSignedIn) {
      this.debugMode &&
        console.log("Debug routerStoreAuthCheck - using cached auth");
      return Promise.resolve(true);
    }

    let allowRoute = false;

    try {
      const checkAuth = await this.handleConnect();
      this.debugMode &&
        console.log("Debug routerStoreAuthCheck checkAuth", checkAuth);
      if (checkAuth) {
        allowRoute = true;
      }
    } catch (err) {
      this.debugMode && console.log("Debug routerStoreAuthCheck Error", err);
      if (err === "Redirect to login") {
        if (this.rootStore.routerStore) {
          this.rootStore.routerStore?.goTo("LoginPage");
        }
      }
    }

    this.debugMode &&
      console.log("Debug routerStoreAuthCheck allowRoute", allowRoute);
    return Promise.resolve(allowRoute);
  };

  /**
   * @function reAuthenticate
   * @description authenticates using current jwt
   */

  reAuthenticate = async (
    authProps: AuthProps
  ): Promise<void | TokenResponse> => {
    this.debugMode && console.log("Debug reAuthenticate");
    this.debugMode && console.log("Debug reAuthenticate authProps", authProps);
    if (this.isAuthenticatePending) {
      this.debugMode && console.log("Debug isAuthenticatePending Already");
      return;
    }
    this.isAuthenticatePending = true;
    let response: void | TokenResponse;
    const refreshToken = authProps?.refreshToken || this.refreshToken || false;
    const jwt = authProps?.jwt || this.token || false;
    const currentUserId =
      authProps?.userId || this.userId || (this.user as User).id || false;
    const deviceId = authProps?.deviceId || this.deviceId || "unregistered";

    if (
      refreshToken &&
      currentUserId &&
      deviceId &&
      this.refreshTokenService?.create
    ) {
      this.debugMode &&
        console.log("Debug reAuthenticate with refreshToken", refreshToken);
      this.debugMode &&
        console.log("Debug reAuthenticate with currentUserId", currentUserId);
      this.debugMode &&
        console.log("Debug reAuthenticate with deviceId", deviceId);
      try {
        const tokenResponse: TokenResponse =
          await this.refreshTokenService.create({
            userId: Number(currentUserId),
            refreshToken: refreshToken,
            deviceId: deviceId,
          });
        runInAction(async () => {
          if (tokenResponse.accessToken) {
            this.debugMode &&
              console.log(
                "Debug reAuthenticate refreshToken response",
                tokenResponse
              );
            response = await this.handleRefreshTokenAuth(
              tokenResponse.accessToken
            );
            return response;
          }
          this.isAuthenticatePending = false;
        });
      } catch (err) {
        runInAction(() => {
          this.setErrorOnAuth(err as Error);
          this.isAuthenticatePending = false;
        });
      }
    } else if (jwt && deviceId) {
      try {
        this.debugMode && console.log("Debug reAuthenticate with jwt", jwt);
        this.debugMode &&
          console.log("Debug reAuthenticate with deviceId", deviceId);
        response = (await this.rootStore.app.authenticate({
          strategy: "jwt",
          deviceId: deviceId,
          accessToken: jwt,
        })) as TokenResponse;
        runInAction(async () => {
          if (response) {
            this.debugMode &&
              console.log("Debug reAuthenticate jwt response", response);
          }
          this.isAuthenticatePending = false;
        });
        return response;
      } catch (err) {
        this.setErrorOnAuth(err as Error);
        this.isAuthenticatePending = false;
      }
    }
  };

  handleRefreshTokenAuth = async (accessToken: string) => {
    this.debugMode &&
      console.log(
        "Debug reAuthenticate with refreshToken response",
        accessToken
      );
    if (!accessToken) throw new Error("Requires accessToken");
    this.token = accessToken;

    // Setup proactive refresh for the new token
    this.setupTokenRefreshTimer(accessToken);

    let response: void | TokenResponse = undefined;

    try {
      this.debugMode &&
        console.log("Debug reAuthenticate with refreshToken setting async jwt");
      const reAuthResultAsync = await this.rootStore.app.authenticate({
        strategy: "jwt",
        deviceId: this.deviceId || "",
        accessToken: accessToken,
      });
      runInAction(() => {
        this.debugMode && console.log("reAuthResultAsync", reAuthResultAsync);
        if (
          reAuthResultAsync &&
          reAuthResultAsync.user &&
          reAuthResultAsync.user.id &&
          reAuthResultAsync.user.id !== this.userId
        ) {
          this.updateUser(reAuthResultAsync.user);
        }
        if ("accessToken" in reAuthResultAsync) {
          response = reAuthResultAsync as TokenResponse;
        } else {
          throw new Error("Invalid token response");
        }
        this.isAuthenticatePending = false;
      });
    } catch (err) {
      this.setErrorOnAuth(err as Error);
      this.isAuthenticatePending = false;
    }
    return response;
  };

  /**
   * @function invalidateRefresh
   * @description invalidateRefresh
   */

  invalidateRefresh = async () => {
    this.debugMode && console.log("Debug invalidateRefresh");
    let response: unknown;
    try {
      const refreshTokenFromStorage = window.localStorage.getItem(
        this.refreshStorageKey
      );
      if (refreshTokenFromStorage && this.refreshTokenService.remove) {
        response = await this.refreshTokenService.remove(null, {
          query: { refreshToken: refreshTokenFromStorage },
        });
        window.localStorage.removeItem(this.refreshStorageKey);
      }
      runInAction(() => {
        this.debugMode && console.log("Debug invalidateRefresh", response);
        if (this.refreshToken) {
          this.setRefreshToken("");
        }
      });
      return response;
    } catch (err) {
      runInAction(() => {
        this.setErrorOnLogout(err as Error);
      });
    }
  };

  /**
   * @function logout
   * @description logout user
   */

  logout = async () => {
    this.debugMode && console.log("Debug logout");
    this.isLogoutPending = true;
    try {
      await this.invalidateRefresh();
      await this.clearAll();
      const response = await this.rootStore.app.logout();

      runInAction(() => {
        this.debugMode && console.log("Debug logout success", response);

        this.isLogoutPending = false;
        if (this.rootStore.routerStore) {
          this.rootStore.routerStore?.goTo("LoginPage");
        }
      });
      return response;
    } catch (err) {
      runInAction(() => {
        this.isLogoutPending = false;
        this.setErrorOnLogout(err as Error);
      });
    }
  };

  pinLogin = async (loginPin: string | number) => {
    const emailLoginStore = this.rootStore.emailLoginStore as ServiceInterface;
    if (!emailLoginStore) throw Error("Requires emailLoginStore");
    if (!loginPin) throw Error("Requires a pin");
    try {
      if (!emailLoginStore || !emailLoginStore?.remove)
        throw new Error("emailLoginStore is undefined");
      return await emailLoginStore?.remove(null, {
        query: {
          requestId: loginPin,
        },
      });
    } catch (err) {
      runInAction(() => {
        this.isLogoutPending = false;
        this.setErrorOnLogout(err as Error);
      });
    }
  };

  requestLoginEmail = async (username: string) => {
    const emailLoginStore = this.rootStore.emailLoginStore as ServiceInterface;
    if (!emailLoginStore || !emailLoginStore.create)
      throw Error("Requires emailLoginStore with create method");
    if (!username) throw Error("Requires valid email");
    try {
      const email = username.includes("@intrasyncindustrial.com")
        ? username
        : `${username}@intrasyncindustrial.com`;
      return await emailLoginStore.create({
        email: email,
      });
    } catch (err) {
      runInAction(() => {
        this.isLogoutPending = false;
        this.setErrorOnLogout(err as Error);
      });
    }
  };

  get userLoaded() {
    return !!this.refreshToken && !!this.userId && !!this.deviceId;
  }

  get userLoadedButNotAuthenticated() {
    return this.userLoaded && !this.isSignedIn;
  }

  get userPermissions(): UserPermission[] {
    if (
      !this.rootStore.userPermissionsStore ||
      !this.rootStore.userPermissionsStore.entities.length
    )
      return [];
    const userPermissionsStore = this.rootStore
      .userPermissionsStore as ServiceStore;
    return (userPermissionsStore.entities as unknown as UserPermission[])
      .slice()
      .filter((p: UserPermission) => p.appName === this.rootStore.appName)
      .filter((p: UserPermission) => p.userId === this.userId);
  }

  get userSubscriptions(): UserSubscription[] {
    const userSubscriptionStore = this.rootStore.userSubscriptionStore as {
      entities: UserSubscription[];
    };
    if (
      (!(
        userSubscriptionStore &&
        typeof userSubscriptionStore === "object" &&
        "entities" in userSubscriptionStore &&
        Array.isArray(userSubscriptionStore.entities)
      ) &&
        !Array.isArray(userSubscriptionStore.entities)) ||
      !userSubscriptionStore.entities.length
    )
      return [];
    return userSubscriptionStore.entities
      .slice()
      .filter((p: UserSubscription) => p.appName === this.rootStore.appName)
      .filter((p: UserSubscription) => p.userId === this.userId);
  }

  get currentUser() {
    return {
      ...this.user,
      permissions: this.userPermissions.map((p: UserPermission) => {
        return { ...p };
      }),
    };
  }

  get currentRoutePermission() {
    const { routerStore } = this.rootStore;
    if (!routerStore) return null;
    return (this.userPermissions as UserPermission[])
      .slice()
      .find(
        (p: UserPermission) => p.location === routerStore.currentPermissionPath
      );
  }

  get userName() {
    if (!this.currentUser || !this.currentUser.email) return "";
    return this.currentUser.email.replace("@intrasyncindustrial.com", "");
  }

  get isAdminUser() {
    if (!this.currentUser || !this.isSignedIn) return false;
    const { adminUser } = this.currentUser;
    return adminUser || false;
  }

  get userId() {
    if (!this.user || !(this.user as User).id) return null;
    return (this.user as User).id;
  }
}

export default AuthStore;
